import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, tokenStore, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader, { resolveImageUrl } from "@/components/ImageUploader";

const emptyEvent = {
  title_pt: "", title_en: "", description_pt: "", description_en: "",
  date: "", time: "19:30", location: "", address: "", image_url: "",
  menu: [], is_published: true,
};
const emptyProduct = {
  name_pt: "", name_en: "", description_pt: "", description_en: "", ingredients: "",
  image_url: "", unit: "", price_retail: 0, price_wholesale: 0, origin: "",
  category: "", is_published: true, sort_order: 0,
};
const emptyPoint = { name: "", address: "", city: "", hours: "", phone: "", is_active: true };
const emptySite = {
  story_pt: "", story_en: "", distribution_intro_pt: "", distribution_intro_en: "",
  gallery_images: [], contact_email: "", contact_phone: "", instagram: "",
};

export default function Admin() {
  const { tr } = useLang();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [points, setPoints] = useState([]);
  const [site, setSite] = useState(emptySite);

  useEffect(() => {
    if (!tokenStore.get()) {
      nav("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); loadAll(); })
      .catch(() => { tokenStore.clear(); nav("/admin/login"); });
    // eslint-disable-next-line
  }, []);

  async function loadAll() {
    const [e, p, r, c, s] = await Promise.all([
      api.get("/events/all"),
      api.get("/products/all"),
      api.get("/wholesale-requests"),
      api.get("/collection-points/all"),
      api.get("/site-content"),
    ]);
    setEvents(e.data);
    setProducts(p.data);
    setRequests(r.data);
    setPoints(c.data);
    setSite({ ...emptySite, ...s.data });
  }

  function logout() {
    tokenStore.clear();
    nav("/admin/login");
  }

  if (!user) return <div className="min-h-screen flex items-center justify-center text-sm text-[#737373]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="page-admin">
      <header className="border-b border-[#E5E0D8] bg-[#FDFBF7] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div>
            <div className="font-serif text-2xl text-[#1A1A1A]">Lama · Admin</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8C7A6B]">{user.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-[11px] uppercase tracking-[0.25em] text-[#1A1A1A] hover:text-[#8C7A6B]" data-testid="admin-back-home">← {tr("nav.home")}</a>
            <Button
              variant="outline"
              onClick={logout}
              className="rounded-none border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] uppercase tracking-[0.25em] text-[11px]"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> {tr("admin.logout")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <Tabs defaultValue="events">
          <TabsList className="bg-transparent border-b border-[#E5E0D8] rounded-none w-full justify-start gap-6 h-auto p-0 mb-10">
            {["events", "products", "requests", "points", "content"].map((k) => (
              <TabsTrigger
                key={k}
                value={k}
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#1A1A1A] data-[state=active]:shadow-none uppercase tracking-[0.25em] text-[11px] px-0 pb-3"
                data-testid={`admin-tab-${k}`}
              >
                {tr(`admin.tab${k.charAt(0).toUpperCase() + k.slice(1)}`)}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="events"><EventsTab events={events} reload={loadAll} /></TabsContent>
          <TabsContent value="products"><ProductsTab products={products} reload={loadAll} /></TabsContent>
          <TabsContent value="requests"><RequestsTab requests={requests} reload={loadAll} /></TabsContent>
          <TabsContent value="points"><PointsTab points={points} reload={loadAll} /></TabsContent>
          <TabsContent value="content"><ContentTab site={site} reload={loadAll} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ----------------- Events tab ----------------- */
function EventsTab({ events, reload }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  function startNew() { setEditing({ ...emptyEvent }); setOpen(true); }
  function startEdit(ev) { setEditing({ ...ev, menu: ev.menu || [] }); setOpen(true); }

  async function save() {
    try {
      const payload = { ...editing, menu: editing.menu.map(m => ({ ...m, price: Number(m.price) || 0 })) };
      delete payload.id;
      delete payload.created_at;
      if (editing.id) await api.put(`/events/${editing.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Saved");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }
  async function del(id) {
    if (!window.confirm("Delete event?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Deleted");
    reload();
  }

  return (
    <div>
      <ListHeader title="Events" onAdd={startNew} testId="events" />
      <Table>
        <TableHeader>
          <TableRow><TableHead>Date</TableHead><TableHead>Title (PT)</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.id} data-testid={`event-row-${e.id}`}>
              <TableCell className="font-mono">{e.date} · {e.time}</TableCell>
              <TableCell>{e.title_pt}</TableCell>
              <TableCell>{e.location}</TableCell>
              <TableCell>{e.is_published ? "Published" : "Draft"}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => startEdit(e)} data-testid={`event-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => del(e.id)} data-testid={`event-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-[#FDFBF7] rounded-none border border-[#1A1A1A] max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit event" : "New event"}</DialogTitle><DialogDescription className="sr-only">Edit event details and menu items.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <AField label="Title PT"><Input value={editing.title_pt} onChange={(e) => setEditing({...editing, title_pt: e.target.value})} data-testid="event-title-pt" /></AField>
                <AField label="Title EN"><Input value={editing.title_en} onChange={(e) => setEditing({...editing, title_en: e.target.value})} data-testid="event-title-en" /></AField>
                <AField label="Date (YYYY-MM-DD)"><Input value={editing.date} onChange={(e) => setEditing({...editing, date: e.target.value})} placeholder="2025-12-22" data-testid="event-date" /></AField>
                <AField label="Time"><Input value={editing.time} onChange={(e) => setEditing({...editing, time: e.target.value})} data-testid="event-time" /></AField>
                <AField label="Location"><Input value={editing.location} onChange={(e) => setEditing({...editing, location: e.target.value})} data-testid="event-location" /></AField>
                <AField label="Address"><Input value={editing.address} onChange={(e) => setEditing({...editing, address: e.target.value})} /></AField>
                <AField label="Image" wide>
                  <ImageUploader
                    value={editing.image_url}
                    onChange={(url) => setEditing({...editing, image_url: url})}
                    testId="event-image"
                  />
                </AField>
                <AField label="Published">
                  <select className="border border-[#E5E0D8] bg-transparent px-2 py-2 w-full" value={editing.is_published ? "1" : "0"} onChange={(e) => setEditing({...editing, is_published: e.target.value === "1"})}>
                    <option value="1">Published</option><option value="0">Draft</option>
                  </select>
                </AField>
              </div>
              <AField label="Description PT"><Textarea rows={3} value={editing.description_pt} onChange={(e) => setEditing({...editing, description_pt: e.target.value})} /></AField>
              <AField label="Description EN"><Textarea rows={3} value={editing.description_en} onChange={(e) => setEditing({...editing, description_en: e.target.value})} /></AField>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B]">Menu items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing({...editing, menu: [...editing.menu, { name_pt: "", name_en: "", description_pt: "", description_en: "", price: 0 }]})} className="rounded-none" data-testid="event-menu-add">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add item
                  </Button>
                </div>
                <div className="space-y-3">
                  {(editing.menu || []).map((m, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start border border-[#E5E0D8] p-3">
                      <Input className="col-span-3" placeholder="Name PT" value={m.name_pt} onChange={(e) => updateMenu(setEditing, editing, idx, "name_pt", e.target.value)} />
                      <Input className="col-span-3" placeholder="Name EN" value={m.name_en} onChange={(e) => updateMenu(setEditing, editing, idx, "name_en", e.target.value)} />
                      <Input className="col-span-2" placeholder="Desc PT" value={m.description_pt} onChange={(e) => updateMenu(setEditing, editing, idx, "description_pt", e.target.value)} />
                      <Input className="col-span-2" placeholder="Desc EN" value={m.description_en} onChange={(e) => updateMenu(setEditing, editing, idx, "description_en", e.target.value)} />
                      <Input className="col-span-1" type="number" step="0.1" placeholder="€" value={m.price} onChange={(e) => updateMenu(setEditing, editing, idx, "price", e.target.value)} />
                      <button type="button" className="col-span-1 self-center text-[#8C7A6B] hover:text-[#1A1A1A]" onClick={() => setEditing({...editing, menu: editing.menu.filter((_, i) => i !== idx)})}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={save} className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none" data-testid="event-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function updateMenu(setEditing, editing, idx, key, value) {
  const next = [...editing.menu];
  next[idx] = { ...next[idx], [key]: value };
  setEditing({ ...editing, menu: next });
}

/* ----------------- Products tab ----------------- */
function ProductsTab({ products, reload }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  async function save() {
    try {
      const payload = { ...editing,
        price_retail: Number(editing.price_retail) || 0,
        price_wholesale: Number(editing.price_wholesale) || 0,
        sort_order: Number(editing.sort_order) || 0,
      };
      delete payload.id; delete payload.created_at;
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }
  async function del(id) {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    reload();
  }

  return (
    <div>
      <ListHeader title="Products" onAdd={() => { setEditing({...emptyProduct}); setOpen(true); }} testId="products" />
      <Table>
        <TableHeader>
          <TableRow><TableHead>Product</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Retail</TableHead><TableHead className="text-right">Wholesale</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {products.map((p) => (
            <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
              <TableCell><div className="font-medium">{p.name_pt}</div><div className="text-xs text-[#737373]">{p.name_en}</div></TableCell>
              <TableCell className="font-mono text-sm">{p.unit}</TableCell>
              <TableCell className="text-right font-mono">€ {Number(p.price_retail).toFixed(2)}</TableCell>
              <TableCell className="text-right font-mono">€ {Number(p.price_wholesale).toFixed(2)}</TableCell>
              <TableCell>{p.is_published ? "Published" : "Draft"}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => { setEditing({...p}); setOpen(true); }} data-testid={`product-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`product-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-[#FDFBF7] rounded-none border border-[#1A1A1A] max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit product" : "New product"}</DialogTitle><DialogDescription className="sr-only">Edit product details, pricing and metadata.</DialogDescription></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4">
              <AField label="Name PT"><Input value={editing.name_pt} onChange={(e) => setEditing({...editing, name_pt: e.target.value})} data-testid="product-name-pt" /></AField>
              <AField label="Name EN"><Input value={editing.name_en} onChange={(e) => setEditing({...editing, name_en: e.target.value})} data-testid="product-name-en" /></AField>
              <AField label="Unit"><Input value={editing.unit} onChange={(e) => setEditing({...editing, unit: e.target.value})} data-testid="product-unit" /></AField>
              <AField label="Origin"><Input value={editing.origin} onChange={(e) => setEditing({...editing, origin: e.target.value})} /></AField>
              <AField label="Category"><Input value={editing.category} onChange={(e) => setEditing({...editing, category: e.target.value})} /></AField>
              <AField label="Sort order"><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({...editing, sort_order: e.target.value})} /></AField>
              <AField label="Retail price €"><Input type="number" step="0.1" value={editing.price_retail} onChange={(e) => setEditing({...editing, price_retail: e.target.value})} data-testid="product-price-retail" /></AField>
              <AField label="Wholesale price €"><Input type="number" step="0.1" value={editing.price_wholesale} onChange={(e) => setEditing({...editing, price_wholesale: e.target.value})} data-testid="product-price-wholesale" /></AField>
              <AField label="Image" wide>
                <ImageUploader
                  value={editing.image_url}
                  onChange={(url) => setEditing({...editing, image_url: url})}
                  testId="product-image"
                />
              </AField>
              <AField label="Description PT" wide><Textarea rows={2} value={editing.description_pt} onChange={(e) => setEditing({...editing, description_pt: e.target.value})} /></AField>
              <AField label="Description EN" wide><Textarea rows={2} value={editing.description_en} onChange={(e) => setEditing({...editing, description_en: e.target.value})} /></AField>
              <AField label="Ingredients" wide><Input value={editing.ingredients} onChange={(e) => setEditing({...editing, ingredients: e.target.value})} /></AField>
              <AField label="Published">
                <select className="border border-[#E5E0D8] bg-transparent px-2 py-2 w-full" value={editing.is_published ? "1" : "0"} onChange={(e) => setEditing({...editing, is_published: e.target.value === "1"})}>
                  <option value="1">Published</option><option value="0">Draft</option>
                </select>
              </AField>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={save} className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none" data-testid="product-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------- Requests tab ----------------- */
function RequestsTab({ requests, reload }) {
  async function setStatus(id, status) {
    await api.put(`/wholesale-requests/${id}/status`, null, { params: { status } });
    toast.success("Updated");
    reload();
  }
  async function del(id) {
    if (!window.confirm("Delete request?")) return;
    await api.delete(`/wholesale-requests/${id}`);
    toast.success("Deleted");
    reload();
  }
  return (
    <div>
      <ListHeader title="B2B & inbound requests" testId="requests" />
      <Table>
        <TableHeader>
          <TableRow><TableHead>Date</TableHead><TableHead>Business / Contact</TableHead><TableHead>Email</TableHead><TableHead>Volume</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((r) => (
            <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
              <TableCell className="font-mono text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
              <TableCell><div className="font-medium">{r.business_name}</div><div className="text-xs text-[#737373]">{r.contact_name} · {r.phone}</div><div className="text-xs text-[#737373] mt-1">{r.message}</div></TableCell>
              <TableCell><a className="underline" href={`mailto:${r.email}`}>{r.email}</a></TableCell>
              <TableCell>{r.estimated_volume}</TableCell>
              <TableCell>
                <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="border border-[#E5E0D8] bg-transparent px-2 py-1 text-sm" data-testid={`request-status-${r.id}`}>
                  <option value="new">new</option><option value="contacted">contacted</option><option value="approved">approved</option><option value="rejected">rejected</option>
                </select>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => del(r.id)} data-testid={`request-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ----------------- Pickup points tab ----------------- */
function PointsTab({ points, reload }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  async function save() {
    try {
      const payload = { ...editing };
      delete payload.id;
      if (editing.id) await api.put(`/collection-points/${editing.id}`, payload);
      else await api.post("/collection-points", payload);
      toast.success("Saved"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }
  async function del(id) { if (!window.confirm("Delete?")) return; await api.delete(`/collection-points/${id}`); reload(); }
  return (
    <div>
      <ListHeader title="Pickup points" onAdd={() => { setEditing({...emptyPoint}); setOpen(true); }} testId="points" />
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Address</TableHead><TableHead>Hours</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {points.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.name}</TableCell>
              <TableCell>{p.address}{p.city && `, ${p.city}`}</TableCell>
              <TableCell>{p.hours}</TableCell>
              <TableCell>{p.is_active ? "Yes" : "No"}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => { setEditing({...p}); setOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => del(p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-[#FDFBF7] rounded-none border border-[#1A1A1A]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Edit point" : "New pickup point"}</DialogTitle><DialogDescription className="sr-only">Edit pickup point details.</DialogDescription></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4">
              <AField label="Name" wide><Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} /></AField>
              <AField label="Address" wide><Input value={editing.address} onChange={(e) => setEditing({...editing, address: e.target.value})} /></AField>
              <AField label="City"><Input value={editing.city} onChange={(e) => setEditing({...editing, city: e.target.value})} /></AField>
              <AField label="Phone"><Input value={editing.phone} onChange={(e) => setEditing({...editing, phone: e.target.value})} /></AField>
              <AField label="Hours" wide><Input value={editing.hours} onChange={(e) => setEditing({...editing, hours: e.target.value})} /></AField>
              <AField label="Active">
                <select className="border border-[#E5E0D8] bg-transparent px-2 py-2 w-full" value={editing.is_active ? "1" : "0"} onChange={(e) => setEditing({...editing, is_active: e.target.value === "1"})}>
                  <option value="1">Yes</option><option value="0">No</option>
                </select>
              </AField>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={save} className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------- Site content tab ----------------- */
function ContentTab({ site, reload }) {
  const [draft, setDraft] = useState(site);
  useEffect(() => setDraft(site), [site]);
  async function save() {
    try {
      await api.put("/site-content", draft);
      toast.success("Saved"); reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }
  return (
    <div className="space-y-6">
      <ListHeader title="Site content" testId="content" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AField label="Story PT" wide><Textarea rows={5} value={draft.story_pt} onChange={(e) => setDraft({...draft, story_pt: e.target.value})} data-testid="content-story-pt" /></AField>
        <AField label="Story EN" wide><Textarea rows={5} value={draft.story_en} onChange={(e) => setDraft({...draft, story_en: e.target.value})} data-testid="content-story-en" /></AField>
        <AField label="Distribution intro PT" wide><Textarea rows={4} value={draft.distribution_intro_pt} onChange={(e) => setDraft({...draft, distribution_intro_pt: e.target.value})} /></AField>
        <AField label="Distribution intro EN" wide><Textarea rows={4} value={draft.distribution_intro_en} onChange={(e) => setDraft({...draft, distribution_intro_en: e.target.value})} /></AField>
        <AField label="Contact email"><Input value={draft.contact_email} onChange={(e) => setDraft({...draft, contact_email: e.target.value})} /></AField>
        <AField label="Contact phone"><Input value={draft.contact_phone} onChange={(e) => setDraft({...draft, contact_phone: e.target.value})} /></AField>
        <AField label="Instagram" wide><Input value={draft.instagram} onChange={(e) => setDraft({...draft, instagram: e.target.value})} /></AField>
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B]">Gallery images</Label>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          {(draft.gallery_images || []).map((u, i) => (
            <div key={i} className="relative border border-[#E5E0D8] bg-[#F4F1EA] group">
              <img
                src={resolveImageUrl(u)}
                alt=""
                className="w-full h-32 object-cover"
                onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
              />
              <button
                type="button"
                onClick={() => setDraft({...draft, gallery_images: draft.gallery_images.filter((_, idx) => idx !== i)})}
                className="absolute top-1 right-1 bg-[#1A1A1A] text-[#FDFBF7] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`gallery-remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="px-2 py-1 text-[10px] font-mono truncate text-[#1A1A1A]/60">{u}</div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <ImageUploader
            value=""
            onChange={(url) => { if (url) setDraft({...draft, gallery_images: [...(draft.gallery_images || []), url]}); }}
            testId="gallery-add"
          />
        </div>
      </div>

      <div className="pt-4">
        <Button onClick={save} className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none px-10 py-6 text-[11px] uppercase tracking-[0.3em]" data-testid="content-save">Save content</Button>
      </div>
    </div>
  );
}

/* ----------------- Shared ----------------- */
function ListHeader({ title, onAdd, testId }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h2 className="font-serif text-3xl text-[#1A1A1A]">{title}</h2>
      {onAdd && (
        <Button onClick={onAdd} className="bg-[#1A1A1A] text-[#FDFBF7] hover:bg-black rounded-none uppercase tracking-[0.25em] text-[11px]" data-testid={`admin-add-${testId}`}>
          <Plus className="w-3.5 h-3.5 mr-2" /> Add
        </Button>
      )}
    </div>
  );
}
function AField({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-[0.25em] text-[#8C7A6B]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
